import React, { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Box } from '@mui/material';
import PropTypes from 'prop-types';

import useOverflowCount from 'hooks/useOverflowCount';
import { measureContainerStyles } from 'hooks/useOverflowCount/constants';
import useTruncatedGeneral from 'hooks/useTruncatedGeneral';

import { selectNavigationConfig } from 'store/global/selectors';

import { NAV_MIN_VISIBLE_ITEMS, OTHER_NAV_ITEM } from './NavigationMenu.constants';
import { getWrapperStyles } from './NavigationMenu.styles';
import { getTransformedNavigation } from './NavigationMenu.utils';
import NavigationMenuItem from './NavigationMenuItem';

const NavigationMenu = ({ isVertical = false, onCloseSidebar }) => {
  const navigation = useSelector(selectNavigationConfig);

  const [onMouseOver, onResetTooltipState, TruncatedProvider] = useTruncatedGeneral();

  const { measureRef, reserveRef, containerRef, maxVisibleItems, gap } = useOverflowCount({
    minVisibleItems: NAV_MIN_VISIBLE_ITEMS,
  });

  const transformedNavigation = useMemo(
    () => getTransformedNavigation({ navigation, visibleCount: maxVisibleItems, isVertical }),
    [navigation, maxVisibleItems, isVertical],
  );

  // An uncertified sub-company user has every navigable surface filtered out (STP-331). Rendering
  // the wrapper regardless leaves a hollow flex slot in the desktop header and a tall blank gap in
  // the mobile drawer, both of which read as a half-broken screen.
  if (!transformedNavigation.length) {
    return null;
  }

  return (
    <>
      {!isVertical && (
        <>
          <Box aria-hidden ref={measureRef} sx={measureContainerStyles}>
            {navigation.map(navItem => (
              <Box key={navItem.id}>
                <NavigationMenuItem isMeasurement {...navItem} />
              </Box>
            ))}
          </Box>
          <Box aria-hidden ref={reserveRef} sx={measureContainerStyles}>
            <NavigationMenuItem isMeasurement {...OTHER_NAV_ITEM} />
          </Box>
        </>
      )}
      <Box
        ref={isVertical ? undefined : containerRef}
        sx={getWrapperStyles({ isVertical, gap })}
        onMouseLeave={onResetTooltipState}
        onMouseOver={onMouseOver}
      >
        <TruncatedProvider />
        {transformedNavigation.map(item => (
          <NavigationMenuItem
            isVertical={isVertical}
            key={item.id}
            onCloseSidebar={onCloseSidebar}
            {...item}
          />
        ))}
      </Box>
    </>
  );
};

NavigationMenu.propTypes = {
  isVertical: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
};

export default memo(NavigationMenu);
